import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase';

const AuthContext = createContext();

// Explicit column list for the shared profile object.
// Using select('*') meant every new column shipped in a migration (linkedin_name,
// terms_*, pet_type, alert_slots, note_credits, …) silently bloated the payload
// that auto-login blocks on — so login got slower with each release. This list is
// the union of every profile field the app actually reads via useAuth(); add a
// column here only when a component starts consuming it.
const PROFILE_COLUMNS =
  'id, role, name, photo_url, profile_complete, terms_version, ' +
  'age, bio, gender, occupation, life_status, cleanliness, schedule, pets, pet_type, ' +
  'instagram, facebook, linkedin, linkedin_name, social_link, ' +
  'desired_city, desired_state, desired_neighborhood, desired_metro_key, desired_location, move_in_start, move_in_end, alert_slots, note_credits';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  // Tracks the currently loaded user so token refreshes for the same user
  // don't re-trigger a full profile fetch + app-wide loading state.
  const loadedUserIdRef = useRef(null);

  const fetchProfile = async (userId) => {
    let timer;
    try {
      // maybeSingle: a missing profile row (brand-new user) is data: null,
      // not an error — .single() would treat it as a failure.
      const profilePromise = supabase
        .from('profiles')
        .select(PROFILE_COLUMNS)
        .eq('id', userId)
        .maybeSingle();
      // Give up after 8 seconds rather than hanging forever
      const timeoutPromise = new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error('profile timeout')), 8000);
      });
      const { data } = await Promise.race([profilePromise, timeoutPromise]);
      setProfile(data || null);
    } catch {
      setProfile(null);
    } finally {
      clearTimeout(timer);
    }
  };

  useEffect(() => {
    // Safety net: if Supabase never responds, stop the spinner after 5s
    const timeout = setTimeout(() => setLoading(false), 5000);

    // Initial session check
    supabase.auth.getSession().then(({ data: { session } }) => {
      clearTimeout(timeout);
      const u = session?.user ?? null;
      setUser(u);
      if (u) {
        loadedUserIdRef.current = u.id;
        fetchProfile(u.id).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    }).catch(() => {
      clearTimeout(timeout);
      setLoading(false);
    });

    // Listen for auth changes — show loading spinner while profile loads
    // so nothing renders until we know who the user is
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        const u = session?.user ?? null;
        if (u) {
          // For a user we've ALREADY loaded, never flip the global loading
          // state — that unmounts the entire app (including any in-progress
          // form like Create Listing) and reads as an aggressive auto-refresh.
          // Supabase re-emits SIGNED_IN on every window focus / tab-visibility
          // regain, and TOKEN_REFRESHED fires ~hourly; in both cases the
          // profile is unchanged, so just update the user object in place.
          if (loadedUserIdRef.current === u.id) {
            setUser(u);
            return;
          }
          setLoading(true);
          await fetchProfile(u.id);
          loadedUserIdRef.current = u.id;
          setUser(u);
          setLoading(false);
        } else {
          loadedUserIdRef.current = null;
          setUser(null);
          setProfile(null);
        }
      }
    );

    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, []);

  const refreshProfile = useCallback(async () => {
    if (user) {
      setLoading(true);
      await fetchProfile(user.id);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (loading) {
    return (
      <div style={{ minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f7f7f7' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
          <div style={{
            width: '40px', height: '40px', borderRadius: '50%',
            border: '3px solid var(--border-hairline)', borderTopColor: 'var(--teal)',
            animation: 'spin 0.8s linear infinite',
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '14px', fontFamily: 'inherit' }}>Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, profile, loading, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
